/**
 * Error Logs page — per docs/specs/error-logs.md
 *
 * Filterable error stream with deep-link to Cloudflare observability.
 *
 * @copyright 2025 Glimbr Pty Ltd
 * @version 2026-04-22-v1
 */
import { Download, ExternalLink, Radio, Search } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Chip } from '../components/chip';
import { useToast } from '../components/toast';
import { useErrorLogs } from '../../lib/api';
import type { ErrorLogRow, ErrorSeverity } from '../../lib/types';
import { ErrorLogDetailModal } from './error-logs-detail-modal';
import { getTopCodeStats, parseLogTs, sanitizeExportField, severityTone } from './infra/error-logs';
import { useAdminSession } from '../../lib/auth';
import { ClientDiagnosticsPanel } from './client-diagnostics';

type TimeRangeFilter = '1h' | '24h' | '7d';
type ServiceFilter =
    | 'all'
    | 'Worker (prod)'
    | 'Worker (dev)'
    | 'Docs worker'
    | 'Audio consumer'
    | 'Frontend'
    | 'Other';
type SeverityFilter = 'all' | ErrorSeverity;
type PreparedErrorLogRow = ErrorLogRow & {
    severity: ErrorSeverity;
    service: ServiceFilter;
    tsSort: number;
};

const TIME_RANGES: Array<{ value: TimeRangeFilter; label: string; ms: number }> = [
    { value: '1h', label: 'Last 1h', ms: 60 * 60_000 },
    { value: '24h', label: 'Last 24h', ms: 24 * 60 * 60_000 },
    { value: '7d', label: 'Last 7d', ms: 7 * 24 * 60 * 60_000 }
];

const SERVICE_OPTIONS: Array<{ value: ServiceFilter; label: string }> = [
    { value: 'all', label: 'All services' },
    { value: 'Worker (prod)', label: 'Worker (prod)' },
    { value: 'Worker (dev)', label: 'Worker (dev)' },
    { value: 'Docs worker', label: 'Docs worker' },
    { value: 'Audio consumer', label: 'Audio consumer' },
    { value: 'Frontend', label: 'Frontend' },
    { value: 'Other', label: 'Other' }
];

const isSeverityFilter = (value: string | null): value is SeverityFilter =>
    value === 'all' ||
    value === 'fatal' ||
    value === 'error' ||
    value === 'warn' ||
    value === 'info';

const isTimeRangeFilter = (value: string | null): value is TimeRangeFilter =>
    value === '1h' || value === '24h' || value === '7d';

const isServiceFilter = (value: string | null): value is ServiceFilter =>
    SERVICE_OPTIONS.some((option) => option.value === value);

const resolveSeverity = (value: ErrorSeverity): ErrorSeverity => {
    if (value === 'fatal' || value === 'error' || value === 'warn' || value === 'info')
        return value;
    return 'info';
};

const resolveService = (value: string): ServiceFilter => {
    if (
        value === 'Worker (prod)' ||
        value === 'Worker (dev)' ||
        value === 'Docs worker' ||
        value === 'Audio consumer' ||
        value === 'Frontend'
    ) {
        return value;
    }

    return 'Other';
};

const getRows = (rows: ErrorLogRow[]): PreparedErrorLogRow[] =>
    rows.map((row) => ({
        ...row,
        severity: resolveSeverity(row.severity),
        service: resolveService(row.service),
        tsSort: parseLogTs(row.ts),
        user_email: row.user_email?.trim() || undefined
    }));

export function ErrorLogsPage() {
    const session = useAdminSession();
    const [params, setParams] = useSearchParams();
    const privileged = session.role === 'owner' || session.role === 'support';
    const source =
        privileged && ['events', 'issues'].includes(params.get('source') || '')
            ? (params.get('source') as 'events' | 'issues')
            : 'backend';
    return (
        <div className="stack-16">
            <nav className="page-actions" aria-label="Error sources">
                {(['backend', ...(privileged ? ['events', 'issues'] : [])] as const).map(
                    (value) => (
                        <button
                            key={value}
                            className={source === value ? 'btn btn-primary' : 'btn btn-ghost'}
                            aria-pressed={source === value}
                            onClick={() => {
                                const next = new URLSearchParams(params);
                                if (value === 'backend') next.delete('source');
                                else next.set('source', value);
                                setParams(next, { replace: true });
                            }}
                        >
                            {value === 'backend'
                                ? 'Backend errors'
                                : value === 'events'
                                  ? 'Client diagnostics'
                                  : 'Grouped issues'}
                        </button>
                    )
                )}
            </nav>
            {source === 'backend' ? (
                <BackendErrorLogsPage />
            ) : (
                <ClientDiagnosticsPanel key={source} kind={source} />
            )}
        </div>
    );
}

function BackendErrorLogsPage() {
    const logs = useErrorLogs();
    const [params, setParams] = useSearchParams();
    const [search, setSearch] = useState(() => params.get('q') || params.get('code') || '');
    const [activeRow, setActiveRow] = useState<ErrorLogRow | null>(null);
    const { toast } = useToast();

    const severityParam = params.get('severity');
    const serviceParam = params.get('service');
    const timeRangeParam = params.get('range');

    const severity = isSeverityFilter(severityParam) ? severityParam : 'all';
    const service = isServiceFilter(serviceParam) ? serviceParam : 'all';
    const timeRange = isTimeRangeFilter(timeRangeParam) ? timeRangeParam : '24h';

    const activeTimeRange =
        TIME_RANGES.find((range) => range.value === timeRange) ?? TIME_RANGES[1];

    useEffect(() => {
        const nextSearch = params.get('q') || params.get('code') || '';
        setSearch((prev) => (prev === nextSearch ? prev : nextSearch));
    }, [params]);

    const preparedRows: PreparedErrorLogRow[] = useMemo(
        () => getRows(logs.data ?? []),
        [logs.data]
    );

    const rows = useMemo(() => {
        const normalizedSearch = search.trim().toLowerCase();
        const sinceMs = Date.now() - activeTimeRange.ms;

        return preparedRows
            .filter((row) => {
                if (severity !== 'all' && row.severity !== severity) return false;
                if (service !== 'all' && row.service !== service) return false;
                if (row.tsSort && row.tsSort < sinceMs) return false;

                if (!normalizedSearch) return true;

                return (
                    row.code.toLowerCase().includes(normalizedSearch) ||
                    row.message.toLowerCase().includes(normalizedSearch) ||
                    (row.user_email ?? '').toLowerCase().includes(normalizedSearch)
                );
            })
            .sort((a, b) => b.tsSort - a.tsSort);
    }, [preparedRows, search, severity, service, activeTimeRange]);

    const topCodes = useMemo(() => getTopCodeStats(rows), [rows]);

    const updateFilterParam = (key: string, value: string, clearValue: string) => {
        const next = new URLSearchParams(params);
        if (value === clearValue) {
            next.delete(key);
        } else {
            next.set(key, value);
        }
        setParams(next, { replace: true });
    };

    const handleExport = () => {
        if (!rows.length) {
            toast('No log rows to export', { kind: 'info' });
            return;
        }

        const header = 'time\tseverity\tservice\tcode\tuser\tcount\ttrace_id\tmessage';
        const lines = rows
            .map((row) =>
                [
                    row.ts,
                    row.severity,
                    sanitizeExportField(row.service),
                    sanitizeExportField(row.code),
                    sanitizeExportField(row.user_email || '—'),
                    row.count,
                    sanitizeExportField(row.trace_id),
                    sanitizeExportField(row.message)
                ].join('\t')
            )
            .join('\n');

        const blob = new Blob([`${header}\n${lines}\n`], {
            type: 'text/plain;charset=utf-8'
        });
        const url = URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        const stamp = [
            new Date().toISOString().slice(0, 10),
            timeRange,
            service.replace(/[^a-z0-9]+/gi, '-').toLowerCase(),
            severity
        ].join('-');

        anchor.href = url;
        anchor.download = `admin-error-logs-${stamp}.log`;
        anchor.click();
        URL.revokeObjectURL(url);
        toast('Exported current log view', { kind: 'success' });
    };

    return (
        <div className="stack-16">
            <header className="page-header">
                <div className="page-title-group">
                    <h1>Error logs</h1>
                    <p>Persisted backend failures with local filters and a Cloudflare handoff.</p>
                </div>
                <div className="page-actions">
                    <button
                        className="btn btn-ghost"
                        type="button"
                        disabled
                        title="Live tail is unsupported; this page polls persisted failures every 30s."
                    >
                        <Radio size={14} /> Live tail
                    </button>
                    <button
                        className="btn btn-ghost"
                        type="button"
                        onClick={handleExport}
                        disabled={!rows.length}
                    >
                        <Download size={14} /> Export .log
                    </button>
                    <a
                        className="btn btn-ghost"
                        href="https://dash.cloudflare.com"
                        target="_blank"
                        rel="noreferrer"
                    >
                        <ExternalLink size={14} /> Cloudflare
                    </a>
                </div>
            </header>

            <section className="alert-bar">
                <div className="alert" data-severity="info">
                    <span className="msg">
                        This stream polls persisted backend failures every 30s. Cloudflare live tail
                        is not wired into the admin UI yet, so the detail modal shows correlation
                        ids and an explicit unsupported-state contract.
                    </span>
                    <span className="meta">read-only</span>
                </div>
            </section>

            <div className="filter-bar">
                <label className="search">
                    <Search size={13} />
                    <input
                        type="search"
                        placeholder="Search by message, code, user email"
                        value={search}
                        onChange={(e) => {
                            const nextSearch = e.target.value;
                            setSearch(nextSearch);
                            updateFilterParam('q', nextSearch, '');
                        }}
                    />
                </label>
                <select
                    className="select"
                    value={severity}
                    onChange={(e) => updateFilterParam('severity', e.target.value, 'all')}
                    aria-label="Severity"
                >
                    <option value="all">All severity</option>
                    <option value="fatal">Fatal</option>
                    <option value="error">Error</option>
                    <option value="warn">Warn</option>
                    <option value="info">Info</option>
                </select>
                <select
                    className="select"
                    value={service}
                    onChange={(e) => updateFilterParam('service', e.target.value, 'all')}
                    aria-label="Service"
                >
                    {SERVICE_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                            {option.label}
                        </option>
                    ))}
                </select>
                <select
                    className="select"
                    value={timeRange}
                    onChange={(e) => updateFilterParam('range', e.target.value, '24h')}
                    aria-label="Time range"
                >
                    {TIME_RANGES.map((option) => (
                        <option key={option.value} value={option.value}>
                            {option.label}
                        </option>
                    ))}
                </select>
                <span className="count">{rows.length} errors</span>
            </div>

            <div className="grid-split">
                <section className="card">
                    <header className="card-header">
                        <h2 className="card-title">Error stream</h2>
                    </header>
                    <div className="table-wrap">
                        <table className="admin-table">
                            <thead>
                                <tr>
                                    <th>Time</th>
                                    <th>Severity</th>
                                    <th>Service</th>
                                    <th>Code · message</th>
                                    <th>User</th>
                                    <th className="numeric">Count</th>
                                    <th />
                                </tr>
                            </thead>
                            <tbody>
                                {rows.length > 0 ? (
                                    rows.map((r) => (
                                        <tr key={r.id} onClick={() => setActiveRow(r)}>
                                            <td className="mono text-muted">{r.ts}</td>
                                            <td>
                                                <Chip tone={severityTone(r.severity)}>
                                                    {r.severity}
                                                </Chip>
                                            </td>
                                            <td className="text-muted">{r.service}</td>
                                            <td>
                                                <div className="user-meta">
                                                    <span className="mono" style={{ fontSize: 11 }}>
                                                        {r.code}
                                                    </span>
                                                    <span className="user-sub">{r.message}</span>
                                                </div>
                                            </td>
                                            <td className="text-muted">{r.user_email ?? '—'}</td>
                                            <td className="numeric">{r.count}</td>
                                            <td
                                                style={{ textAlign: 'right' }}
                                                onClick={(e) => e.stopPropagation()}
                                            >
                                                <button
                                                    className="btn btn-sm btn-ghost"
                                                    onClick={() => setActiveRow(r)}
                                                >
                                                    <ExternalLink size={11} />
                                                </button>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan={7} className="empty-state">
                                            No error rows match the current filters.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </section>

                <section className="card">
                    <header className="card-header">
                        <h2 className="card-title">Top codes · {activeTimeRange.label}</h2>
                    </header>
                    <div className="card-body tight">
                        {topCodes.length > 0 ? (
                            topCodes.map((codeStat) => (
                                <div
                                    key={codeStat.code}
                                    style={{
                                        display: 'grid',
                                        gridTemplateColumns: '1fr auto',
                                        padding: '10px 16px',
                                        borderBottom: '1px solid var(--border)',
                                        fontSize: 12
                                    }}
                                >
                                    <div style={{ minWidth: 0 }}>
                                        <span className="mono" style={{ display: 'block' }}>
                                            {codeStat.code}
                                        </span>
                                        <span className="text-muted" style={{ fontSize: 10 }}>
                                            last seen {codeStat.lastTs || '—'}
                                        </span>
                                    </div>
                                    <Chip
                                        tone={
                                            codeStat.count > 20
                                                ? 'danger'
                                                : codeStat.count > 8
                                                  ? 'warning'
                                                  : 'muted'
                                        }
                                    >
                                        {codeStat.count}
                                    </Chip>
                                </div>
                            ))
                        ) : (
                            <div className="empty-state">
                                <div className="title">No grouped codes yet</div>
                            </div>
                        )}
                    </div>
                </section>
            </div>

            <ErrorLogDetailModal row={activeRow} onClose={() => setActiveRow(null)} />
        </div>
    );
}
